import React, { useRef, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Avatar,
  Tooltip,
  ListSubheader,
  Collapse,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  Search as SearchIcon,
  Inventory as InventoryIcon,
  Person as PersonIcon,
  ExitToApp as LogoutIcon,
  People as PeopleIcon,
  BarChart as BarChartIcon,
  Notifications as NotificationsIcon,
  Message as MessageIcon,
  AccountCircle as ProfileIcon,
  Receipt as TransactionsIcon,
  LocalShipping as OrdersIcon,
  Build as ServiceIcon,
  Business as SuppliersIcon,
  Warning as AlertsIcon,
  ExpandLess,
  ExpandMore,
  Speed as SpeedIcon,
  Event as EventIcon,
  CalendarToday as CalendarTodayIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFirebase } from '../../contexts/FirebaseContext';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

interface MenuItem {
  title: string;
  path?: string;
  icon?: React.ReactElement;
  items?: MenuItem[];
  badge?: number;
}

const drawerWidth = 280;

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [open, setOpen] = React.useState(true);
  const [openMenus, setOpenMenus] = React.useState<{ [key: string]: boolean }>({});
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, signOut } = useFirebase();
  const theme = useTheme();
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Restore scroll position when component mounts and set up scroll listener
  useEffect(() => {
    const savedPosition = sessionStorage.getItem('sidebarScrollPosition');
    if (savedPosition && sidebarRef.current) {
      sidebarRef.current.scrollTop = parseInt(savedPosition);
    }

    // Add scroll event listener
    const handleScroll = () => {
      if (sidebarRef.current) {
        sessionStorage.setItem('sidebarScrollPosition', sidebarRef.current.scrollTop.toString());
      }
    };

    const sidebarElement = sidebarRef.current;
    if (sidebarElement) {
      sidebarElement.addEventListener('scroll', handleScroll);
    }

    // Clean up event listener
    return () => {
      if (sidebarElement) {
        sidebarElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, []);

  // Save scroll position when navigating
  const saveScrollPosition = () => {
    if (sidebarRef.current) {
      sessionStorage.setItem('sidebarScrollPosition', sidebarRef.current.scrollTop.toString());
    }
  };

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleMenuClick = (title: string) => {
    setOpenMenus(prev => ({ ...prev, [title]: !prev[title] }));
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getMenuItems = (role: string): MenuItem[] => {
    const dealershipItems: MenuItem[] = [
      {
        title: 'Overview',
        items: [
          {
            title: 'Dashboard',
            path: '/dealership/dashboard',
            icon: <SpeedIcon />,
          },
          {
            title: 'Parts Alerts',
            path: '/dealership/alerts',
            icon: <AlertsIcon />,
            badge: 3,
          },
        ],
      },
      {
        title: 'Operations',
        items: [
          {
            title: 'Transactions',
            path: '/dealership/transactions',
            icon: <TransactionsIcon />,
          },
          {
            title: 'Inventory',
            path: '/dealership/inventory',
            icon: <InventoryIcon />,
          },
          {
            title: 'Orders',
            path: '/dealership/orders',
            icon: <OrdersIcon />,
          },
        ],
      },
      {
        title: 'Services',
        items: [
          {
            title: 'Service History',
            path: '/dealership/service-history',
            icon: <ServiceIcon />,
          },
          {
            title: 'Customers',
            path: '/dealership/customers',
            icon: <PeopleIcon />,
          },
          {
            title: 'Suppliers',
            path: '/dealership/suppliers',
            icon: <SuppliersIcon />,
          },
        ],
      },
      {
        title: 'Analytics',
        items: [
          {
            title: 'Reports',
            path: '/dealership/reports',
            icon: <BarChartIcon />,
          },
        ],
      },
    ];

    const repairShopItems: MenuItem[] = [
      {
        title: 'Repair Shop',
        items: [
          {
            title: 'Repair Jobs',
            path: '/repair-shop/jobs',
            icon: <ServiceIcon />,
          },
          {
            title: 'Capacity Management',
            path: '/repair-shop/capacity',
            icon: <SpeedIcon />,
          },
          {
            title: 'Parts Order',
            path: '/repair-shop/parts-order',
            icon: <InventoryIcon />,
          },
          {
            title: 'Schedule',
            path: '/repair-shop/schedule',
            icon: <EventIcon />,
          },
          {
            title: 'Service History',
            path: '/repair-shop/service-history',
            icon: <HistoryIcon />,
          },
          {
            title: 'Availability Calendar',
            path: '/repair-shop/availability',
            icon: <CalendarTodayIcon />,
          },
        ],
      },
    ];

    const spareShopItems: MenuItem[] = [
      {
        title: 'Spare Parts Shop',
        items: [
          {
            title: 'Parts Search',
            path: '/spare-shop/parts-search',
            icon: <SearchIcon />,
          },
          {
            title: 'Stock Management',
            path: '/spare-shop/stock',
            icon: <InventoryIcon />,
          },
          {
            title: 'Supplier Management',
            path: '/spare-shop/suppliers',
            icon: <SuppliersIcon />,
          },
          {
            title: 'Analytics',
            path: '/spare-shop/analytics',
            icon: <BarChartIcon />,
          },
        ],
      },
    ];

    const commonItems: MenuItem[] = [
      {
        title: 'Communication',
        items: [
          {
            title: 'Messages',
            path: '/messages',
            icon: <MessageIcon />,
            badge: 4,
          },
          {
            title: 'Notifications',
            path: '/notifications',
            icon: <NotificationsIcon />,
            badge: 2,
          },
        ],
      },
      {
        title: 'Account',
        items: [
          {
            title: 'Profile',
            path: '/profile',
            icon: <ProfileIcon />,
          },
        ],
      },
    ];

    // If user is admin, show all menu items
    if (role === 'admin') {
      return [...dealershipItems, ...repairShopItems, ...spareShopItems, ...commonItems];
    }

    // For regular users, show menu items based on their role
    switch (role) {
      case 'dealership':
        return [...dealershipItems, ...commonItems];
      case 'repair-shop':
        return [...repairShopItems, ...commonItems];
      case 'spare-shop':
        return [...spareShopItems, ...commonItems];
      default:
        return [...dealershipItems, ...commonItems];
    }
  };

  const renderMenuItem = (item: MenuItem, depth: number = 0) => {
    if (item.items) {
      return (
        <React.Fragment key={item.title}>
          <ListSubheader
            sx={{
              bgcolor: alpha(theme.palette.background.paper, 0.9),
              color: theme.palette.primary.main,
              fontSize: '0.75rem',
              letterSpacing: '0.1em',
              lineHeight: '32px',
              mt: depth === 0 ? 2 : 0,
              position: 'sticky',
              top: depth * 32,
              zIndex: 10 - depth,
              backdropFilter: 'blur(8px)',
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            {item.title}
          </ListSubheader>
          {item.items.map((subItem) => renderMenuItem(subItem, depth + 1))}
        </React.Fragment>
      );
    }

    return (
      <Tooltip key={item.title} title={item.title} placement="right" arrow>
        <ListItem
          button
          onClick={() => {
            if (item.path) {
              saveScrollPosition();
              navigate(item.path);
            }
          }}
          selected={location.pathname === item.path}
          sx={{
            my: 0.5,
            mx: 1,
            borderRadius: 1,
            '&.Mui-selected': {
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.25),
              },
            },
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.1),
            },
          }}
        >
          <ListItemIcon>
            {item.badge ? (
              <Badge badgeContent={item.badge} color="error">
                {item.icon}
              </Badge>
            ) : (
              item.icon
            )}
          </ListItemIcon>
          <ListItemText 
            primary={item.title}
            sx={{
              '& .MuiTypography-root': {
                fontSize: '0.9rem',
                fontWeight: location.pathname === item.path ? 600 : 400,
              },
            }}
          />
        </ListItem>
      </Tooltip>
    );
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        sx={{
          width: open ? `calc(100% - ${drawerWidth}px)` : '100%',
          ml: open ? `${drawerWidth}px` : 0,
          boxShadow: 'none',
          transition: 'all 0.3s ease-in-out',
          backdropFilter: 'blur(10px)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: 'linear-gradient(90deg, transparent, rgba(255,215,0,0.5), transparent)',
          },
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              color="inherit"
              aria-label="toggle drawer"
              onClick={handleDrawerToggle}
              edge="start"
              sx={{
                mr: 2,
                transition: 'transform 0.3s ease-in-out',
                '&:hover': {
                  transform: 'rotate(180deg)',
                },
              }}
            >
              {open ? <ChevronLeftIcon /> : <MenuIcon />}
            </IconButton>
            <Typography 
              variant="h5" 
              noWrap 
              component="div"
              sx={{ 
                background: 'linear-gradient(45deg, #FFD700 30%, #FFF 90%)',
                backgroundSize: '200% auto',
                animation: 'shine 3s linear infinite',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 600,
                letterSpacing: '0.05em',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -4,
                  left: 0,
                  width: '100%',
                  height: '2px',
                  background: 'linear-gradient(90deg, transparent, #FFD700, transparent)',
                  transform: 'scaleX(0)',
                  transition: 'transform 0.3s ease-in-out',
                },
                '&:hover::after': {
                  transform: 'scaleX(1)',
                },
              }}
            >
              SpareSync Elite
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton 
              color="inherit" 
              onClick={handleLogout}
              sx={{
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'rotate(90deg)',
                  color: theme.palette.primary.main,
                },
              }}
            >
              <LogoutIcon />
            </IconButton>
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
                '&:hover': {
                  transform: 'scale(1.1)',
                  boxShadow: '0 0 20px rgba(255,215,0,0.4)',
                },
              }}
            >
              {currentUser?.email?.[0].toUpperCase()}
            </Avatar>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="persistent"
        anchor="left"
        open={open}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            border: 'none',
            backgroundImage: `linear-gradient(180deg, 
              ${alpha(theme.palette.background.paper, 0.96)} 0%, 
              ${alpha(theme.palette.background.default, 0.96)} 100%)`,
            backdropFilter: 'blur(20px)',
            transition: 'all 0.3s ease-in-out',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '2px',
              background: 'linear-gradient(90deg, transparent, rgba(255,215,0,0.3), transparent)',
            },
          },
        }}
      >
        <Box 
          ref={sidebarRef}
          sx={{ 
            overflow: 'auto', 
            height: '100%', 
            pt: 2,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255,215,0,0.3)',
              borderRadius: '3px',
              '&:hover': {
                background: 'rgba(255,215,0,0.5)',
              },
            },
          }}
        >
          <List>
            {getMenuItems(currentUser?.displayName || 'dealership').map((item) =>
              renderMenuItem(item)
            )}
          </List>
        </Box>
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: `calc(100% - ${drawerWidth}px)`,
          marginLeft: open ? `${drawerWidth}px` : 0,
          marginTop: '64px',
          minHeight: '100vh',
          background: `
            radial-gradient(circle at 50% 0%, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 50%),
            radial-gradient(circle at 100% 50%, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 50%)
          `,
          transition: 'all 0.3s ease-in-out',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(255,215,0,0.2), transparent)',
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}; 